/**
 * Walks an array of sibling course records in order and tags each with a
 * `canStart` boolean — true while every preceding record is Completed, then
 * false from the first not-Completed record onward (that one is still
 * startable; the ones after it are locked).
 *
 * @param {Array} records - Sibling course records, in order.
 * @returns {Array} Same records with `canStart` added.
 */
export function tagCanStart(records) {
  let canStartNext = true
  return records.map((record) => {
    const canStart = canStartNext
    if (record.status !== 'Completed') {
      canStartNext = false
    }
    return { ...record, canStart }
  })
}

/**
 * Adds `canStart` flag to each course record in a certificate.
 * Records can only be started if all preceding records are completed.
 *
 * @param {Object} certificate - A student certificate with courseRecords
 * @returns {Object} Certificate with canStart added to each courseRecord
 */
export function addCanStartToCourseRecords(certificate) {
  return { ...certificate, courseRecords: tagCanStart(certificate.courseRecords) }
}

/**
 * Finds the next module a student should actually click into for this
 * certificate — the first record that isn't Completed and is currently
 * startable (canStart). Returns null when every module is done or no
 * record is unlocked.
 *
 * Requires the certificate's courseRecords to have been tagged via
 * tagCanStart / addCanStartToCourseRecords. Both surfaces that use this
 * (Courses page, Dashboard) already do.
 *
 * @param {Object} certificate - studentCertificate with courseRecords.
 * @returns {Object|null}
 */
export function getNextActionableCourseRecord(certificate) {
  const records = certificate?.courseRecords ?? []
  return records.find((r) => r.status !== 'Completed' && r.canStart) ?? null
}

/**
 * Whether a specific course record is startable, based on its siblings under
 * the same certificate. Source of truth matches the list/dashboard rule:
 * you can only start the next module once every prior module is Completed.
 *
 * Returns true when the record isn't in its sibling list (defensive — stale
 * data, race after a cert reassignment) so the UI doesn't silently lock a
 * record we don't have context for.
 *
 * @param {Object} courseRecord - Record with studentCertificate.courseRecords
 *   expanded.
 * @returns {boolean}
 */
export function canStartCourseRecord(courseRecord) {
  const siblings = courseRecord?.studentCertificate?.courseRecords ?? []
  const idx = siblings.findIndex((s) => s.id === courseRecord?.id)
  if (idx === -1) return true
  return siblings.slice(0, idx).every((s) => s.status === 'Completed')
}

/**
 * Finds the next incomplete sibling course record inside the same student
 * certificate. Requires the passed courseRecord to have an expanded
 * studentCertificate.courseRecords array.
 *
 * @param {Object} courseRecord - A student's course record with its parent
 *   studentCertificate expanded (including sibling courseRecords).
 * @returns {Object|null} The next incomplete sibling, or null if none remain.
 */
export function getNextIncompleteCourseRecord(courseRecord) {
  const siblings = courseRecord?.studentCertificate?.courseRecords
  if (!siblings?.length) return null
  const currentIndex = siblings.findIndex((r) => r.id === courseRecord.id)
  if (currentIndex === -1) return null
  return siblings.slice(currentIndex + 1).find((r) => r.status !== 'Completed') || null
}
